import { Button, Modal, ServiceError, TOTPCodeInput } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";

import { useTracker } from "../../hooks/useTracker";
import { TOTPErrorCode, useDeleteTOTP } from "../../hooks/useTwoFactor";
import { ModalClosedAction, ModalShownAction } from "../../lib/TrackingAdapter";

import style from "./TwoFactorDisableModal.module.css";

export type TwoFactorDisableModalProps = {
  open: boolean;
  onClose: () => void;
};

type FormData = {
  code: string;
};

export default function TwoFactorDisableModal({ open, onClose }: TwoFactorDisableModalProps) {
  const tracker = useTracker();
  const {
    control,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      code: "",
    },
  });

  const { mutateAsync: mutateDeleteTOTP, isPending } = useDeleteTOTP();
  const codeValue = watch("code");

  const onSubmit = async (data: FormData) => {
    try {
      await mutateDeleteTOTP({ code: data.code });
      onClose();
    } catch (error) {
      let message = "Something went wrong. Please try again.";
      if (error instanceof ServiceError) {
        const errorCode = error.serviceErrorResponse.code;
        if (errorCode === TOTPErrorCode.InvalidCode) {
          message = "Invalid code. Please try again.";
        }
      }
      setError("code", { message });
    }
  };

  return (
    <Modal
      open={open}
      onOpen={() => tracker.trackModalShown(ModalShownAction.TWO_FACTOR_DISABLE)}
      onClose={() => {
        tracker.trackModalClosed(ModalClosedAction.TWO_FACTOR_DISABLE);
        onClose();
      }}
      size="medium"
      title="Are you sure you want to disable 2FA?"
      footer={
        <div className={style.footer}>
          <Button
            variant="red"
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
            disabled={isPending || codeValue.length !== 6}
            formNoValidate
          >
            Disable anyway
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              tracker.trackTwoFactorDisableAct({ action: "keep_enabled" });
              onClose();
            }}
          >
            Keep 2FA enabled
          </Button>
        </div>
      }
    >
      <div className={style.container}>
        <div className={style.content}>
          <div className={style.description}>
            Two-factor authentication adds an extra layer of security to your account. Without it,
            your account is more vulnerable to unauthorized access.
            <div className={style.instruction}>
              Enter the 6-digit code from your authenticator app to disable the 2FA
            </div>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TOTPCodeInput
                  onChange={field.onChange}
                  onComplete={(value) => {
                    field.onChange(value);
                  }}
                  error={Boolean(errors.code)}
                  errorMessage={errors.code?.message}
                  disabled={isPending}
                  autoFocus
                />
              )}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
